// src/events/organiser_profile.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

import {
  FiUser, FiMail, FiPhone, FiMapPin, FiHome, FiHash,
  FiImage, FiCopy, FiRefreshCw, FiLock, FiX, FiCheckCircle,
  FiFileText, FiCreditCard, FiLogOut, FiEye, FiEyeOff
} from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";

/* ────────────────────────────────────────────────────────── */
/* Brand / Theme                                              */
/* ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  tertiary: "#982a18",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  success: "#16a34a",
  warn: "#b91c1c",
};

const storage = getStorage();

/* ────────────────────────────────────────────────────────── */
/* Helpers                                                    */
/* ────────────────────────────────────────────────────────── */
function titleCase(s){ return (s||"").replace(/\w\S*/g, t => t[0].toUpperCase() + t.slice(1).toLowerCase()); }
function mask(s, show=4){ if(!s) return "—"; const str=String(s); return `${"•".repeat(Math.max(0, str.length-show))}${str.slice(-show)}`; }
function b64(bytes){ return btoa(String.fromCharCode(...new Uint8Array(bytes))); }
function fromUtf8(str){ return new TextEncoder().encode(str); }
function genSalt(len=16){ const b=new Uint8Array(len); crypto.getRandomValues(b); return b; }
function genPassword(len=12){
  const L="ABCDEFGHJKLMNPQRSTUVWXYZ";
  const l="abcdefghijkmnpqrstuvwxyz";
  const d="23456789";
  const s="!@#$%^&*?";
  const all=L+l+d+s;
  const pick=(set)=>set[Math.floor(Math.random()*set.length)];
  let out = pick(L)+pick(l)+pick(d)+pick(s);
  for(let i=4;i<len;i++) out += pick(all);
  return out.split("").sort(()=>Math.random()-0.5).join("");
}
async function sha256(bytes){ return await crypto.subtle.digest("SHA-256", bytes); }

function isPdf(url=""){ return /\.pdf(\?|$)/i.test(url); }
function isImg(url=""){ return /\.(png|jpe?g|webp)(\?|$)/i.test(url); }

/* Upload avatar -> returns URL */
async function uploadAvatar(organiserUID, file){
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `organiser_signup/${organiserUID}/profile/avatar.${ext}`;
  const r = sRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  return await getDownloadURL(r);
}

/* ────────────────────────────────────────────────────────── */
/* Component                                                  */
/* ────────────────────────────────────────────────────────── */
export default function OrganiserProfile({ organiserUID, contactName }) {
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // password modal
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState(false);

  // reveal bank account
  const [showAcc, setShowAcc] = useState(false);

  // doc viewer modal
  const [docModal, setDocModal] = useState({ open:false, label:"", url:"" });

  // logout success modal
  const [logoutOK, setLogoutOK] = useState(false);

  // fetch profile by organiserUID
  useEffect(() => {
    if (!organiserUID || organiserUID === "—") return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const ref = doc(db, "organiser_signup", organiserUID);
        const snap = await getDoc(ref);
        if (!mounted) return;
        setProfile({ id: snap.id, ...(snap.data()||{}) });
      } catch (e) {
        console.error("Profile load error:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [organiserUID]);

  const display = useMemo(() => {
    const g = profile?.general || {};
    const org = g.organisation || {};
    const c = g.contact || {};
    const bank = g.bank || {};
    const docs = profile?.documents || {};
    const agr = profile?.agreement || {};
    return {
      contactName: c.name || contactName || "—",
      contactEmail: c.email || "—",
      contactMobile: c.mobile || "—",
      photoUrl: c.photoUrl || "",
      companyName: org.name || "—",
      companyPAN: org.pan || "—",
      state: org.state || "—",
      address: org.address || "—",
      gstList: Array.isArray(org.gst) ? org.gst : [],
      itr2yrs: !!org.itr_last2yrs,
      gstDecl: !!org.gst_declaration_accepted,
      bankBeneficiary: bank.beneficiary || "—",
      bankType: bank.accountType || "—",
      bankName: bank.bankName || "—",
      bankAcc: bank.accountNumber || "—",
      bankIfsc: bank.ifsc || "—",
      panUrl: docs.panUrl || "",
      chequeUrl: docs.cancelledChequeUrl || "",
      agreementUrl: agr.signedUrl || "",
      hasPassword: !!profile?.auth?.passwordHash,
      passwordSetAt: profile?.auth?.passwordSetAt || null,
      createdAt: profile?.createdAt || null,
      updatedAt: profile?.updatedAt || null,
      status: profile?.status || "—",
      step: profile?.step || "—",
    };
  }, [profile, contactName]);

  /* Avatar handler */
  async function onPickAvatar(file){
    if(!file) return;
    if(file.size > 2*1024*1024){ alert("Avatar must be ≤ 2MB"); return; }
    if(!/(png|jpe?g|webp)$/i.test(file.name)){ alert("Avatar must be PNG/JPG/WEBP"); return; }
    try{
      setAvatarUploading(true);
      const url = await uploadAvatar(organiserUID, file);
      const ref = doc(db, "organiser_signup", organiserUID);
      await updateDoc(ref, {
        "general.contact.photoUrl": url,
        updatedAt: serverTimestamp()
      });
      setProfile(p => ({
        ...(p||{}),
        general: {
          ...(p?.general||{}),
          contact: { ...(p?.general?.contact||{}), photoUrl: url }
        }
      }));
    }catch(e){
      console.error(e);
      alert("Could not upload avatar. Try again.");
    }finally{
      setAvatarUploading(false);
    }
  }

  /* Password modal openers */
  function openGeneratePassword(){
    setPwd(genPassword());
    setPwd2("");
    setPwdError("");
    setPwdOpen(true);
  }
  function openChangePassword(){
    setPwd("");
    setPwd2("");
    setPwdError("");
    setPwdOpen(true);
  }

  /* Save password -> salted SHA-256 */
  async function handleUpdatePassword(){
    setPwdError("");
    const p = String(pwd || "");
    const q = String(pwd2 || "");
    if(p.length < 8){ setPwdError("Password must be at least 8 characters."); return; }
    if(p !== q){ setPwdError("Passwords do not match."); return; }

    try{
      setSaving(true);
      const salt = genSalt(16);
      const toHash = new Uint8Array([...salt, ...fromUtf8(p)]);
      const digest = await sha256(toHash);
      const hashB64 = b64(digest);
      const saltB64 = b64(salt);

      const ref = doc(db, "organiser_signup", organiserUID);
      await updateDoc(ref, {
        "auth.passwordHash": hashB64,
        "auth.passwordSalt": saltB64,
        "auth.passwordSetAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // reflect locally
      setProfile(pv => ({
        ...(pv||{}),
        auth: { ...(pv?.auth||{}), passwordHash: hashB64, passwordSalt: saltB64 }
      }));

      setPwdOpen(false);
      setPwdSuccess(true);
    }catch(e){
      console.error(e);
      setPwdError("Could not update password. Try again.");
    }finally{
      setSaving(false);
    }
  }

  /* Logout: clear session + success popup then redirect */
  function handleLogout(){
    try {
      localStorage.removeItem("events_session");
      sessionStorage.clear?.();
    } catch {}
    setLogoutOK(true);
    // auto-redirect shortly
    setTimeout(() => {
      window.location.replace("/events/login");
    }, 1100);
  }

  /* Timestamps */
  function fmtTs(ts){
    try{
      const d = ts?.toDate ? ts.toDate() : null;
      if(!d) return "—";
      return d.toLocaleString();
    }catch{ return "—"; }
  }

  /* Open doc viewer modal */
  function openDoc(label, url){
    if(!url) return;
    setDocModal({ open:true, label, url });
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12, minHeight:0 }}>
      {/* Gradient accent */}
      <div style={{
        height:6, borderRadius:999, background:`linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
        opacity:.9
      }}/>

      {/* Header card */}
      <Card>
        <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <div style={{ position:"relative" }}>
            <div style={{
              width: 84, height: 84, borderRadius: "50%", overflow:"hidden",
              border: `2px solid ${COLORS.border}`, background:"#fff",
              display:"grid", placeItems:"center"
            }}>
              {display.photoUrl ? (
                <img src={display.photoUrl} alt="Profile" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
              ) : (
                <FiUser size={36} color={COLORS.subtext}/>
              )}
            </div>
            <label
              title="Upload photo"
              style={{
                position:"absolute", right:-6, bottom:-6,
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
                width: 36, height: 36, borderRadius: "50%",
                display:"grid", placeItems:"center", color:"#fff", cursor:"pointer",
                boxShadow:"0 6px 18px rgba(242,12,83,.25)",
                opacity: avatarUploading ? .6 : 1
              }}
            >
              <FiImage />
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display:"none" }}
                disabled={avatarUploading}
                onChange={(e)=>onPickAvatar(e.target.files?.[0]||null)}
              />
            </label>
          </div>

          <div style={{ display:"grid", gap:4, minWidth:240 }}>
            <div style={{ fontSize:22, fontWeight:900, color: COLORS.text }}>
              {titleCase(display.contactName)}
            </div>
            <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
              <FaBuilding/>{titleCase(display.companyName)}
            </div>
            <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
              <FiHash/>{organiserUID || "—"}
            </div>
          </div>

          <div style={{ marginLeft:"auto", display:"grid", gap:6 }}>
            {/* Password summary */}
            <div style={{
              display:"flex", alignItems:"center", gap:8,
              background:"#fff", border:`1px solid ${COLORS.border}`, borderRadius:10, padding:"8px 12px"
            }}>
              <FiLock/>
              <span style={{ letterSpacing:3 }}>********</span>
              {!display.hasPassword ? (
                <button onClick={openGeneratePassword} style={pillBtn}>
                  Generate password
                </button>
              ) : (
                <button onClick={openChangePassword} style={pillBtn}>
                  Change password
                </button>
              )}
            </div>
            <div style={{ fontSize:12, color: COLORS.subtext }}>
              Status: {titleCase(profile?.status || "pending")} · Updated: {fmtTs(profile?.updatedAt)}
            </div>
          </div>
        </div>
      </Card>

      {/* Content rows (scrolls within drawer) */}
      <div style={{ display:"grid", gap:12 }}>
        <GridTwo>
          <Card>
            <Section title="Personal Details" icon={<FiUser/>}>
              <Row icon={<FiUser/>}  label="Name"   value={titleCase(display.contactName)} />
              <Row icon={<FiMail/>}  label="Email"  value={display.contactEmail} />
              <Row icon={<FiPhone/>} label="Mobile" value={display.contactMobile} />
            </Section>
          </Card>

          <Card>
            <Section title="Organisation" icon={<FaBuilding/>}>
              <Row icon={<FaBuilding/>} label="Company Name" value={titleCase(display.companyName)} />
              <Row icon={<FiHash/>}      label="PAN"          value={display.companyPAN} />
              <Row icon={<FiMapPin/>}    label="State/UT"     value={display.state} />
              <RowStacked
                icon={<FiHome/>}
                label="Address"
                value={display.address}
              />
              {display.gstList?.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={muted}>GST Registrations</div>
                  <ul style={{ margin:0, marginTop:6, paddingLeft:18, color: COLORS.text }}>
                    {display.gstList.map((g, i)=>(
                      <li key={i}>{g.number || g.gst} {g.state ? `(${g.state})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>
          </Card>
        </GridTwo>

        <GridTwo>
          <Card>
            <Section title="Bank" icon={<FiCreditCard/>}>
              <Row icon={<FiCreditCard/>} label="Beneficiary" value={titleCase(display.bankBeneficiary)} />
              <Row icon={<FiCreditCard/>} label="Bank"       value={`${display.bankName} · ${display.bankType}`} />
              {/* Account with eye toggle */}
              <div style={{ display:"grid", gridTemplateColumns:"140px 1fr", gap:10, padding:"6px 0", alignItems:"center" }}>
                <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color: COLORS.primary }}><FiCreditCard/></span> Account
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color: COLORS.text, fontFamily:"monospace" }}>
                    {showAcc ? (display.bankAcc || "—") : mask(display.bankAcc)}
                  </span>
                  <button
                    type="button"
                    onClick={()=>setShowAcc(v=>!v)}
                    title={showAcc ? "Hide" : "Show"}
                    style={iconGhostBtn}
                  >
                    {showAcc ? <FiEyeOff/> : <FiEye/>}
                  </button>
                </div>
              </div>
              <Row icon={<FiCreditCard/>} label="IFSC" value={display.bankIfsc} />
            </Section>
          </Card>

          <Card>
            <Section title="Documents" icon={<FiFileText/>}>
              <DocButton icon={<FiFileText/>} label="PAN Document"      url={display.panUrl}    onOpen={openDoc} />
              <DocButton icon={<FiFileText/>} label="Cancelled Cheque"  url={display.chequeUrl} onOpen={openDoc} />
              <DocButton icon={<FiFileText/>} label="Signed Agreement"  url={display.agreementUrl} onOpen={openDoc} />
            </Section>
          </Card>
        </GridTwo>

        <div style={{ display:"flex", justifyContent:"flex-end" }}>
          <button onClick={handleLogout} style={logoutBtn}><FiLogOut/> Logout</button>
        </div>
      </div>

      {saving && <div style={savingMask}>Saving…</div>}

      {/* PASSWORD MODAL */}
      {pwdOpen && (
        <div style={modalBackdrop} onClick={()=>setPwdOpen(false)}>
          <div style={modalCard} onClick={(e)=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, color: COLORS.text }}>
                {display.hasPassword ? "Change Password" : "Generate Password"}
              </h3>
              <button onClick={()=>setPwdOpen(false)} style={iconBtn}><FiX/></button>
            </div>

            <div style={{ marginTop: 8 }}>
              {!display.hasPassword && (
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <button type="button" onClick={()=>setPwd(genPassword())} style={ghostBtn} title="Regenerate">
                    <FiRefreshCw/> Regenerate
                  </button>
                  <button type="button" onClick={()=>navigator.clipboard?.writeText(pwd)} style={ghostBtn} title="Copy">
                    <FiCopy/> Copy
                  </button>
                </div>
              )}

              <label style={{ display:"block", marginTop: 6 }}>
                <span style={labelStyle}><FiLock/> New password</span>
                <input
                  value={pwd}
                  onChange={(e)=>setPwd(e.target.value)}
                  placeholder="Enter new password"
                  style={inputStyle}
                  type="password"
                />
              </label>

              <label style={{ display:"block", marginTop: 6 }}>
                <span style={labelStyle}><FiLock/> Confirm password</span>
                <input
                  value={pwd2}
                  onChange={(e)=>setPwd2(e.target.value)}
                  placeholder="Re-enter password"
                  style={inputStyle}
                  type="password"
                />
              </label>

              {pwdError && <div style={{ color: COLORS.warn, marginTop: 6 }}>{pwdError}</div>}

              <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop: 12 }}>
                <button onClick={()=>setPwdOpen(false)} style={ghostBtn}>Cancel</button>
                <button onClick={handleUpdatePassword} style={primaryBtn}>Update Password</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PASSWORD SUCCESS POPUP */}
      {pwdSuccess && (
        <div style={modalBackdrop} onClick={()=>setPwdSuccess(false)}>
          <div style={{ ...modalCard, maxWidth: 460 }} onClick={(e)=>e.stopPropagation()}>
            <div style={{ display:"grid", placeItems:"center", marginTop: 6 }}>
              <div style={{
                width: 72, height: 72, borderRadius:"50%",
                background:"rgba(22,163,74,.12)", color: COLORS.success,
                display:"grid", placeItems:"center"
              }}>
                <FiCheckCircle size={34}/>
              </div>
            </div>
            <h3 style={{ textAlign:"center", margin:"12px 0 6px" }}>Password updated</h3>
            <p style={{ textAlign:"center", color: COLORS.subtext, margin:0 }}>
              Your password has been set. It will be shown as <b>********</b> next time.
            </p>
            <div style={{ display:"flex", justifyContent:"flex-end", marginTop: 12, gap:8 }}>
              <button onClick={()=>setPwdSuccess(false)} style={ghostBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* DOC VIEWER MODAL (in-app) */}
      {docModal.open && (
        <div style={modalBackdrop} onClick={()=>setDocModal({ open:false, label:"", url:"" })}>
          <div style={{ ...modalCard, maxWidth: 760, padding: 12 }} onClick={(e)=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 6px 8px" }}>
              <h3 style={{ margin:0, color: COLORS.text }}>{docModal.label}</h3>
              <button onClick={()=>setDocModal({ open:false, label:"", url:"" })} style={iconBtn}><FiX/></button>
            </div>

            <div style={{
              background:"#fff",
              border:`1px solid ${COLORS.border}`,
              borderRadius:10,
              padding:10,
              display:"grid",
              placeItems:"center"
            }}>
              {/* Standard size viewer */}
              {isPdf(docModal.url) ? (
                <iframe
                  title="document"
                  src={docModal.url}
                  style={{ width:"680px", maxWidth:"90vw", height:"70vh", border:"none", borderRadius:8 }}
                />
              ) : isImg(docModal.url) ? (
                <img
                  alt="document"
                  src={docModal.url}
                  style={{ width:"680px", maxWidth:"90vw", height:"auto", maxHeight:"70vh", objectFit:"contain", borderRadius:8 }}
                />
              ) : (
                <div style={{ color: COLORS.subtext, padding: 12 }}>
                  Preview not available. <a href={docModal.url} target="_blank" rel="noreferrer" style={linkBtn}>Open in new tab</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LOGOUT SUCCESS */}
      {logoutOK && (
        <div style={modalBackdrop} onClick={()=>{}}>
          <div style={{ ...modalCard, maxWidth: 420 }}>
            <div style={{ display:"grid", placeItems:"center", marginTop: 6 }}>
              <div style={{
                width: 72, height: 72, borderRadius:"50%",
                background:"rgba(22,163,74,.12)", color: COLORS.success,
                display:"grid", placeItems:"center"
              }}>
                <FiCheckCircle size={34}/>
              </div>
            </div>
            <h3 style={{ textAlign:"center", margin:"12px 0 6px" }}>Logged out</h3>
            <p style={{ textAlign:"center", color: COLORS.subtext, margin:0 }}>
              Redirecting to login…
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Small Parts                                                */
/* ────────────────────────────────────────────────────────── */
function Section({ title, icon, children }){
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10, display:"grid", placeItems:"center",
          border:`1px solid ${COLORS.border}`, background:"#fff", color: COLORS.primary
        }}>
          {icon}
        </div>
        <h4 style={{ margin:0, fontSize:16, color: COLORS.text }}>{title}</h4>
      </div>
      {children}
    </div>
  );
}
function Row({ icon, label, value, multiline }){
  return (
    <div style={{ display:"grid", gridTemplateColumns:"140px 1fr", gap:10, padding:"6px 0", alignItems:"start" }}>
      <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ color: COLORS.primary }}>{icon}</span> {label}
      </div>
      <div style={{ color: COLORS.text, whiteSpace: multiline ? "pre-wrap" : "nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
        {value || "—"}
      </div>
    </div>
  );
}
/* Stacked row for big address block */
function RowStacked({ icon, label, value }){
  return (
    <div style={{ padding:"8px 0" }}>
      <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ color: COLORS.primary }}>{icon}</span> {label}
      </div>
      <div style={{
        marginTop:6,
        border:`1px solid ${COLORS.border}`,
        background:"#fff",
        borderRadius:10,
        padding:12,
        color: COLORS.text,
        whiteSpace:"pre-wrap",
        lineHeight:1.5
      }}>
        {value || "—"}
      </div>
    </div>
  );
}
function DocButton({ icon, label, url, onOpen }){
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 0", gap:12 }}>
      <div style={{ color: COLORS.subtext, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ color: COLORS.primary }}>{icon}</span> {label}
      </div>
      {url ? (
        <button type="button" onClick={()=>onOpen(label, url)} style={linkBtn}>View</button>
      ) : (
        <span style={{ color: COLORS.subtext }}>—</span>
      )}
    </div>
  );
}
function Card({ children }){
  return (
    <div style={{
      background: COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14,
      padding: 16, boxShadow:"0 1px 2px rgba(0,0,0,.05)"
    }}>{children}</div>
  );
}
function GridTwo({ children }){
  return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>{children}</div>;
}

/* ────────────────────────────────────────────────────────── */
/* Styles shared with your app                                */
/* ────────────────────────────────────────────────────────── */
const inputStyle = {
  width: "100%", height: 44, padding: "0 12px", marginTop: 6,
  border: `1px solid ${COLORS.border}`, borderRadius: 10, outline: "none", background: "#fff",
};
const labelStyle = { fontSize: 13, color: COLORS.text, display:"flex", alignItems:"center", gap:8 };
const linkBtn = {
  border: "none", background: "transparent",
  color: COLORS.primary, fontWeight: 800, cursor: "pointer", textDecoration: "none",
};
const ghostBtn = {
  border: `1px solid ${COLORS.border}`, background: "#fff", color: COLORS.text,
  height: 42, padding: "0 16px", borderRadius: 10, cursor:"pointer",
  display:"inline-flex", alignItems:"center", gap:8,
};
const iconGhostBtn = {
  ...ghostBtn,
  width:36, height:36, padding:0, borderRadius:8
};
const primaryBtn = {
  ...ghostBtn,
  color: "#fff",
  border: "none",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow:"0 8px 22px rgba(242,12,83,.18)"
};
const pillBtn = {
  ...ghostBtn,
  height: 32, padding: "0 12px", borderRadius: 999, fontWeight: 800,
};
const logoutBtn = {
  ...ghostBtn,
  border: `1px solid ${COLORS.warn}33`,
  color: COLORS.warn,
};
const iconBtn = { ...ghostBtn, width:36, height:36, padding:0, borderRadius:8 };

const modalBackdrop = {
  position: "fixed", inset: 0, background:"rgba(0,0,0,.45)", display:"grid", placeItems:"center", zIndex: 100, padding: 16
};
const modalCard = {
  width:"100%", maxWidth: 560, background:"#fff", border:`1px solid ${COLORS.border}`,
  borderRadius: 14, padding: 16, boxShadow:"0 10px 30px rgba(0,0,0,.25)"
};
const savingMask = {
  position:"fixed", inset:0, background:"rgba(255,255,255,.5)", display:"grid", placeItems:"center",
  fontWeight:900, color: COLORS.text, zIndex: 120
};
const muted = { fontSize:12, color: COLORS.subtext };
